import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { GeneralLayout, ListItem, ListTemplate, SearchBar } from '@room/shared-ui';
import { normalizePluginType, registerHomeTile } from '@room/shared-ui';
import { fetchPluginCatalog } from '../../lib/pluginCatalog';
import { PluginDetail, type PluginRecord } from './PluginDetail';

registerHomeTile({
  id: 'home-store',
  label: 'Store',
  icon: '🛒',
  route: '/store',
  section: 'dock',
  order: 10,
});

const TYPE_LABELS = {
  bridge: { label: 'Bridges', icon: '🌉' },
  game: { label: 'Games', icon: '🎮' },
  tool: { label: 'Tools', icon: '🔧' },
  utility: { label: 'Utilities', icon: '⚡' },
};

export default function StorePage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPluginId, setSelectedPluginId] = useState<string | null>(null);
  const [showMobileDetail, setShowMobileDetail] = useState(false);
  const {
    data: catalog,
    isLoading,
  } = useQuery({
    queryKey: ['plugin-catalog'],
    queryFn: fetchPluginCatalog,
    refetchInterval: 30000,
  });

  const storePlugins = catalog?.items ?? [];

  const getPluginIcon = (plugin: PluginRecord): string => {
    const icon = plugin.manifest?.ui?.sidebar?.icon;
    if (icon) return icon;
    const type = normalizePluginType(plugin.type, plugin.manifest?.type);
    return TYPE_LABELS[type]?.icon || '🧩';
  };

  const filteredPlugins = useMemo(() => {
    if (!searchQuery.trim()) return storePlugins;
    const query = searchQuery.toLowerCase();
    return storePlugins.filter((plugin) => {
      const haystack = `${plugin.name ?? ''} ${plugin.description ?? ''}`.toLowerCase();
      return haystack.includes(query);
    });
  }, [searchQuery, storePlugins]);

  const selectedPlugin = useMemo(
    () => storePlugins.find((plugin) => plugin.id === selectedPluginId) ?? null,
    [storePlugins, selectedPluginId]
  );

  const handleSelectPlugin = (pluginId: string) => {
    setSelectedPluginId(pluginId);
    setShowMobileDetail(true);
  };

  return (
    <GeneralLayout
      leftHidden={showMobileDetail}
      rightActive={showMobileDetail}
      left={(
        <>
          <div className="general-layout__toolbar">
            <SearchBar
              placeholder="Search plugins..."
              value={searchQuery}
              onSearch={setSearchQuery}
              debounce={300}
              fullWidth
            />
          </div>
          <ListTemplate
            items={filteredPlugins}
            loading={isLoading}
            emptyState={(
              <div className="empty-state">
                <div className="empty-icon">🧩</div>
                <p>No plugins found.</p>
              </div>
            )}
            listClassName="general-list"
            renderItem={(plugin) => (
              <ListItem
                leading={<span>{getPluginIcon(plugin)}</span>}
                title={plugin.name ?? plugin.id}
                subtitle={plugin.description ?? 'No description provided.'}
                trailing={plugin.version ?? '—'}
                active={plugin.id === selectedPluginId}
                primaryAction={{
                  onClick: () => handleSelectPlugin(plugin.id),
                  ariaLabel: `View details for ${plugin.name ?? plugin.id}`,
                }}
              />
            )}
            keyExtractor={(plugin) => plugin.id}
            variant="compact"
          />
        </>
      )}
      right={(
        <PluginDetail
          plugin={selectedPlugin}
          icon={selectedPlugin ? getPluginIcon(selectedPlugin) : '🧩'}
          typeLabel={
            selectedPlugin
              ? TYPE_LABELS[normalizePluginType(selectedPlugin.type, selectedPlugin.manifest?.type)]
                ?.label
              : undefined
          }
          showBackButton={showMobileDetail}
          onBack={() => setShowMobileDetail(false)}
        />
      )}
    />
  );
}
